import { AlpineComponent } from "@nxtlvlsoftware/alpine-typescript";
import { v4 } from "uuid";
import { FileData, QueuedFile } from "./file-upload.types";
import axios from "axios";
import { FormsService } from "./api";

export class FileUploadComponent extends AlpineComponent {
    private abortUploadController = new AbortController();

    public QueuedFiles: QueuedFile[] = [];
    public UploadedFiles: FileData[] = [];
    public UploadedFilesValue: string[] = [];
    public CompletedFileCount: number = 0;
    public TotalFileCount: number = 0;
    constructor(uploadedFiles: QueuedFile[] | null = null) {		
        super();
		if(uploadedFiles) {
			this.QueuedFiles = uploadedFiles;
			this.UploadedFiles = this.QueuedFiles.filter((f) => f.status === "uploaded")
												 .map((f) => ({ fileName: f.fileName, key: f.key } as FileData));
			this.UploadedFilesValue = this.UploadedFiles.map((f) => f.fileName + ":" + f.key);
		}
    }

    protected fileDropZone = this.binding({
        ["x-on:click"]() {
            const fileInput = this.$refs.fileInput as HTMLInputElement;
            fileInput.click();
        },
        ["x-on:dragover.prevent"](event: DragEvent) {
            this.HandleDragOver(event);
        },
        ["x-on:drop.prevent"](event: DragEvent) {
            this.HandleDrop(event);
        },
    });
    protected fileUploadField = this.binding({
        ["x-ref"]: "fileInput",
        ["x-on:change"]() {
            const element = this.$el as HTMLInputElement;
            const files = element.files;

            if (!files) return;

            this.AddFilesToQueue(files);
        },
    });

    public AddFilesToQueue(files: FileList) {
        for (let i = 0; i < files.length; i++) {
            const file = files.item(i);
            if (!file) continue;

            const exists = this.QueuedFiles.find(
                (f) =>
                    (f.file?.name ?? f.fileName) === file.name
            );
            if (exists) continue;

            const queuedFile = {
                key: v4(),
                file: file,
				fileName: file.name,
                status: "queued",
            } as QueuedFile;
            this.QueuedFiles.push(queuedFile);
        }

        this.startUploadQueue();
    }

    public HandleDrop(event: DragEvent) {
        const files = event.dataTransfer?.files;
        if (files) {
            this.AddFilesToQueue(files);
        }
    }
    public HandleDragOver(event: DragEvent) {
        if (event.dataTransfer === null) return;

        const fileItems = [...event.dataTransfer?.items].filter(
            (item) => item.kind === "file"
        );
        if (fileItems.length > 0) {
            event.dataTransfer.dropEffect = "copy";
        } else {
            event.dataTransfer.dropEffect = "none";
        }
    }

    public async startUploadQueue() {
        if (this.QueuedFiles.length === 0) return;
        if (this.QueuedFiles.find((f) => f.status === "uploading")) return;

        const nextFile = this.QueuedFiles.find((f) => f.status === "queued" && f.file !== null);
        if (!nextFile) return;

        this.uploadNextFile(nextFile);
    }
    public async uploadNextFile(nextFile: QueuedFile) {
		if(nextFile.file === null) return;
        nextFile.status = "uploading";
        const formData = new FormData();
        formData.append("file", nextFile.file);
        formData.append("fileId", nextFile.key);
        formData.append("fileName", nextFile.fileName);

        try {
            const response = await axios.post(
                "/umbraco/site/api/v1/forms/upload",
                formData,
                {
                    signal: this.abortUploadController.signal,
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress: (progressEvent) => {
                        if (progressEvent.total) {
                            const percentCompleted = (progressEvent.loaded * 100) / progressEvent.total;
                            nextFile.percentProgress = percentCompleted;
                            nextFile.fileProgress = progressEvent.loaded;
                        }
                    },
                }
            );
			if(response.status == 200) {
				nextFile.status = "uploaded";
			} else {
				nextFile.status = "error";
				nextFile.errorMessage = `Status: ${response.statusText}`;
			}
        } catch (error) {
            if (axios.isAxiosError(error) && !axios.isCancel(error)) {
                nextFile.status = "error";
                nextFile.errorMessage = `Upload failed: ${error.message}`;
            }
        }

        this.UploadedFiles = this.QueuedFiles.filter((f) => f.status === "uploaded")
											 .map((f) => ({ fileName: f.fileName, key: f.key } as FileData));
        this.UploadedFilesValue = this.UploadedFiles.map((f) => f.fileName + ":" + f.key);

		this.UpdateFileCounts();

        this.startUploadQueue();
    }

    public async RemoveFileFromQueue(file: QueuedFile) {
        if (file.status === "uploading") {
            file.status = "canceled";
            this.abortUploadController.abort();
            this.abortUploadController = new AbortController();
        } else if (file.status === "uploaded") {
            await FormsService.deleteById({
                path:{
                    id: file.key
                }
            });
        }

        this.QueuedFiles = this.QueuedFiles.filter((f) => f.key !== file.key);
        this.UploadedFilesValue = this.UploadedFiles.map((f) => f.fileName + ":" + f.key);

		this.UpdateFileCounts();

        this.startUploadQueue();
    }

    public UpdateFileCounts() {
        this.CompletedFileCount = this.QueuedFiles.filter(
            (f) => f.status === "uploaded"
        ).length;
        this.TotalFileCount = this.QueuedFiles.length;
    }

    public ReadableFileSize(size: number): string {
        if (size === 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(size) / Math.log(k));
        return parseFloat((size / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    }
}
